import {
	ResponsiveContainer,
	ComposedChart,
	XAxis,
	YAxis,
	Bar,
	Line,
	LabelList,
	Tooltip,
	BarChart,
	Legend,
} from "recharts";
import { numberFormatter } from "../../utils";
import divider from "../../assets/divider-logo.3699f955.svg";
import moment from "moment";

interface DateRange {
  baseline: number | null;
  current: number | null;
}
interface Props {
	chartData: any;
	heading: any;
	dateRange?:DateRange
}
interface T {
	quarter: string;
	actual: number;
	forecastedSpend: number;
	spendNeededForNextTier: number;
	spendShift: number;
	targetGrowthSpend: number;
}
const CustomLabel = ({ x, y, value, index, data }: any) => {
	const d = 50; // distance from point to label
	const angle = 90; // angle in degrees
	const angleRad = (angle * Math.PI) / 180; // convert to radians
	const xOffset = d * Math.cos(angleRad);
	const yOffset = d * Math.sin(angleRad);

	// Find first and last indices where avgSellPrice_Prev is not null
	let firstValidIndex = -1;
	let lastValidIndex = -1;
	for (let i = 0; i < data.length; i++) {
		if (
			data[i].avgSellPrice_Prev !== null &&
			data[i].avgSellPrice_Prev !== undefined
		) {
			firstValidIndex = i;
			break;
		}
	}
	for (let i = data.length - 1; i >= 0; i--) {
		if (
			data[i].avgSellPrice_Prev !== null &&
			data[i].avgSellPrice_Prev !== undefined
		) {
			lastValidIndex = i;
			break;
		}
	}
	const showLabel =
		(firstValidIndex !== -1 && index === firstValidIndex) ||
		(lastValidIndex !== -1 && index === lastValidIndex);

	return (
		<g>
			{showLabel && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x + xOffset}
						y2={y - yOffset}
						stroke="gray"
						opacity={0.5}
					/>
					<text
						x={x + xOffset}
						y={y - yOffset - 5}
						fill="gray"
						fontSize={"1.4vh"}
						opacity={0.8}
						textAnchor="middle"
					>
						{numberFormatter(value, 1, "$")}
					</text>
				</>
			)}
		</g>
	);
};

const CustomLabel2 = ({ x, y, value, index, data }: any) => {
	const d = 30; // distance from point to label
	const angle = 110; // angle in degrees
	const angleRad = (angle * Math.PI) / 180; // convert to radians
	const xOffset = d * Math.cos(angleRad);
	const yOffset = d * Math.sin(angleRad);

	// Find first and last indices where avgSellPrice_Curr is not null
	let firstValidIndex = -1;
	let lastValidIndex = -1;
	for (let i = 0; i < data.length; i++) {
		if (
			data[i].avgSellPrice_Curr !== null &&
			data[i].avgSellPrice_Curr !== undefined
		) {
			firstValidIndex = i;
			break;
		}
	}
	for (let i = data.length - 1; i >= 0; i--) {
		if (
			data[i].avgSellPrice_Curr !== null &&
			data[i].avgSellPrice_Curr !== undefined
		) {
			lastValidIndex = i;
			break;
		}
	}
	const showLabel =
		(firstValidIndex !== -1 && index === firstValidIndex) ||
		(lastValidIndex !== -1 && index === lastValidIndex);

	return (
		<g>
			{showLabel && (
				<>
					<line
						x1={x}
						y1={y}
						x2={x + xOffset}
						y2={y - yOffset}
						stroke="#012169"
						opacity={0.5}
					/>
					<text
						x={x + xOffset}
						y={y - yOffset - 5}
						fill="#012169"
						fontSize={"1.4vh"}
						opacity={0.8}
						textAnchor="middle"
					>
						{numberFormatter(value, 1, "$")}
					</text>
				</>
			)}
		</g>
	);
};
const PricingExecution = (props: Props) => {
	let minValue = 0;
	let maxValue = 0;
	let data: any = props?.chartData?.finalData;
	let customTicks: any = [];
	let formattedData: any = [];

	if (data?.length > 0) {
		// Create formattedData with formattedWeek property
		formattedData = data.map((item: any) => ({
			...item,
			formattedWeek: item.monthly
				? moment.utc(item.week).format("MMM")
				: moment.utc(item.week).format("MMM DD"),
		}));

		const minPrev = formattedData.reduce((min: any, obj: any) => {
			return obj.avgSellPrice_Prev < min ? obj.avgSellPrice_Prev : min;
		}, Infinity);
		const minCurr = formattedData.reduce((min: any, obj: any) => {
			return obj.avgSellPrice_Curr < min ? obj.avgSellPrice_Curr : min;
		}, Infinity);
		const maxPrev = formattedData.reduce((max: any, obj: any) => {
			return obj.avgSellPrice_Prev > max ? obj.avgSellPrice_Prev : max;
		}, -Infinity);
		const maxCurr = formattedData.reduce((max: any, obj: any) => {
			return obj.avgSellPrice_Curr > max ? obj.avgSellPrice_Curr : max;
		}, -Infinity);
		minValue = minPrev > minCurr ? minCurr : minPrev;
		maxValue = maxPrev > maxCurr ? maxPrev : maxCurr;

		// Determine customTicks
		if (formattedData[0].monthly) {
			customTicks = formattedData.map(
				(item: { formattedWeek: any }) => item.formattedWeek
			);
		} else {
			const indicesToShow = [0]; // always show first
			// Add every third index starting from 0 + 3
			for (let i = 4; i < formattedData.length; i += 4) {
				indicesToShow.push(i);
			}

			customTicks = indicesToShow.map(
				(index) => formattedData[index].formattedWeek
			);
		}
	}

	let heading = props?.heading;
	const customTooltip = ({ active, payload }: any) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload;

			const popupStyle = {
				width: "fit-content",
				background: "white",
				border: "1px solid #ccc",
				boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
				borderRadius: "1vh",
				color: "black",
				padding: 0,
			};

			return (
				<div style={popupStyle}>
					{/* Header Row */}
					<div className="w-full flex justify-start h-[3vh] items-center bg-[#f0f4f8] py-[3vh] px-[1vh] textSmall">
						<div className="font-bold uppercase">
							{data.monthly ? "Month" : "Week"}:
						</div>
						<div
							className="font-bold ml-1"
							style={{ color: "#3f3f3f" }}
						>
							{data.formattedWeek}
						</div>
					</div>

					{/* Data Rows */}
					<div className="h-fit p-[1vh] mt-[.5vh] mb-[.5vh]">
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Sell Price {props?.dateRange?.current||2025} ($/Unit) :{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(
									data.avgSellPrice_Curr,
									2,
									"$"
								)}
							</label>
						</p>
						<p className="label" style={{ padding: 0, margin: 0 }}>
							<label
								className="graph-label-small"
								style={{
									color: "#5a5a5a",
									fontWeight: "normal",
								}}
							>
								Sell Price {props?.dateRange?.baseline||2024} ($/Unit) :{" "}
							</label>
							<label
								className="graph-label-small"
								style={{
									color: "#3f3f3f",
									fontWeight: "bold",
								}}
							>
								{numberFormatter(
									data.avgSellPrice_Prev,
									2,
									"$"
								)}
							</label>
						</p>
					</div>
				</div>
			);
		}
		return null;
	};

	return (
		<div className={`w-full graphBox h-[100%]`}>
			<div className="headSpace">
				<span className="headContent !text-[1vw]"> {heading}</span>
			</div>
			<ResponsiveContainer
				width="100%"
				height="65%"
				className="bg-[#ffff]"
			>
				<ComposedChart
					data={formattedData}
					margin={{
						top: 60,
						right: 40,
						left: 40,
					}}
				>
					<XAxis
						dataKey="formattedWeek"
						stroke="black"
						className="graph-label-small "
						interval={1}
						ticks={customTicks}
					/>
					<YAxis
						hide
						// domain={[minValue - (minValue/2), maxValue + minValue/3]}
						domain={[
							(dataMin: any) => dataMin / 1.1,
							(dataMax: any) => dataMax * 1.0,
						]}
						fontSize="1.5vh"
						tickFormatter={(value: number) =>
							numberFormatter(value, 0, "$")
						}
					/>
					<Tooltip
						contentStyle={{
							color: "white",
							backgroundColor: "rgb(26, 25, 22)",
						}}
						cursor={{ strokeDasharray: "3 3", fill: "#212121" }}
						content={customTooltip}
					/>

					<Line
						type="linear"
						dataKey="avgSellPrice_Prev"
						stroke="gray"
						name={`2024`}
						strokeDasharray={"0"}
						connectNulls={true}
						strokeWidth={2}
						dot={($) => (
							<CustomDot {...$} data={data} color={"gray"} />
						)}
					>
						<LabelList
							dataKey="avgSellPrice_Prev"
							content={(props) => (
								<CustomLabel {...props} data={formattedData} />
							)}
						/>
					</Line>
					<Line
						type="linear"
						dataKey="avgSellPrice_Curr"
						name={`2025`}
						stroke="#012169"
						strokeDasharray={"0"}
						connectNulls={false}
						strokeWidth={2}
						dot={($) => (
							<CustomDot {...$} data={data} color={"#012169"} />
						)}
					>
						<LabelList
							dataKey="avgSellPrice_Curr"
							content={(props) => (
								<CustomLabel2 {...props} data={formattedData} />
							)}
						/>
					</Line>
				</ComposedChart>
			</ResponsiveContainer>
			<div className="contentFooter !justify-between">
				<span className="text-[#666666] italic textSmall">
					*Excludes new customers
				</span>
				<CustomLegend dateRange={props?.dateRange}></CustomLegend>
			</div>
		</div>
	);
};
const CustomLegend = (props:any) => (
	<div
		style={{
			display: "flex",
			gap: "2rem",
			alignItems: "center",
			color: "black",
			fontSize: "0.8rem",
		}}
	>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "gray",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.baseline||2024}
		</span>
		<span>
			<span
				style={{
					display: "inline-block",
					width: 10,
					height: 18,
					background: "#012169",
					marginRight: 8,
					verticalAlign: "middle",
				}}
			/>
			{props?.dateRange?.current||2025}
		</span>
	</div>
);

const CustomDot = (props: any) => {
	const { cx, cy, index, data, color } = props;
	// Only render for first and last point
	if (index === 0 || index === data.length - 1) {
		return <circle cx={cx} cy={cy} r={5} fill={color} stroke="none" />;
	}
	return null;
};

export default PricingExecution;
